import { localizeDescription, type ParamSpec } from "@cfg/design-sdk";
import { useLocale } from "@cfg/configurator-ui";
import type { DesignDescription } from "@cfg/kernel/worker";
import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { useApp } from "./app-context.tsx";
import { getKernelClient } from "./kernel.ts";

/** What lists of saved brackets need to name them readably: the spec, defaults and groups of one design version. */
export interface DescribedVersion {
  spec: ParamSpec[];
  defaults: Record<string, unknown>;
  ui: { groups?: { params: string[]; collapsed?: boolean }[] };
}

/**
 * Descriptions of several `designId@version`s, translated for the active locale, keyed by
 * that key. Loads each bundle into the preview worker only (describe, no build); keys whose
 * bundle can't be resolved or loaded are simply missing, so callers fall back to stored names.
 */
export function useDesignDescriptions(keys: { designId: string; version: string }[]): Map<string, DescribedVersion> {
  const { registry } = useApp();
  const { designs } = useLocale();
  const sig = [...new Set(keys.map((k) => `${k.designId}@${k.version}`))].join("|");
  const unique = useMemo(() => (sig ? sig.split("|") : []).map((key) => ({ designId: key.slice(0, key.lastIndexOf("@")), version: key.slice(key.lastIndexOf("@") + 1) })), [sig]);
  const results = useQueries({
    queries: unique.map((k) => ({
      queryKey: ["design-description", k.designId, k.version],
      staleTime: Infinity,
      retry: false,
      queryFn: async (): Promise<DesignDescription | null> => {
        const target = await registry.resolve(k.designId, k.version);
        if (!target) return null;
        return getKernelClient().preview.load(`${k.designId}@${k.version}`, target.bundleUrl);
      },
    })),
  });
  const stamp = results.map((r) => r.dataUpdatedAt).join(",");
  return useMemo(() => {
    const out = new Map<string, DescribedVersion>();
    unique.forEach((k, i) => {
      const raw = results[i]?.data;
      if (!raw) return;
      const d = designs ? localizeDescription(raw, designs) : raw;
      out.set(`${k.designId}@${k.version}`, { spec: d.spec as ParamSpec[], defaults: d.defaults, ui: (d.ui ?? {}) as DescribedVersion["ui"] });
    });
    return out;
    // `results` is a new array every render; `stamp` changes when any of their data does.
  }, [unique, designs, stamp]);
}
